// src/components/Settings/Backup.tsx
import React, { useState } from 'react';
import { Download, Upload, CheckCircle, FileJson, Lock, Key } from 'lucide-react';
import toast from 'react-hot-toast';

interface BackupProps {
  userId: string;
  username: string;
  password: string;
  currentDEK: CryptoKey | null;
  onImportSuccess?: () => void;
}

export const Backup: React.FC<BackupProps> = ({
  userId,
  username,
  password,
  currentDEK,
  onImportSuccess,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState<{ success: number; error: number } | null>(null);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [inputPassword, setInputPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  // ========== 工具函數 ==========

  // ✅ 安全 Base64 解碼
  const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
    if (!base64) return new ArrayBuffer(0);
    try {
      const binaryString = atob(base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes.buffer;
    } catch (e) {
      return new ArrayBuffer(0);
    }
  };

  // ArrayBuffer 轉 Base64
  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  // ✅ 用 DEK 解密文字（用於 title, content, tags 等）
  const decryptWithDEK = async (base64Data: string, dek: CryptoKey): Promise<string> => {
    if (!base64Data || base64Data.length < 10) {
      return '';
    }
    try {
      const combined = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
      if (combined.length < 13) {
        return '';
      }
      const iv = combined.slice(0, 12);
      const encrypted = combined.slice(12);
      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        dek,
        encrypted
      );
      return new TextDecoder().decode(decrypted);
    } catch (e) {
      console.warn('解密失敗:', e);
      return '';
    }
  };

  // ✅ 用 DEK 加密文字（用於 title, content, tags 等）
  const encryptWithDEK = async (data: string, dek: CryptoKey): Promise<string> => {
    if (!data) return '';
    const encoder = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      dek,
      encoder.encode(data)
    );
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encrypted), iv.length);
    return btoa(String.fromCharCode(...combined));
  };

  // ✅ 用 DEK 解密二進位資料（專門用於 note_key）
  const decryptBinaryWithDEK = async (base64Data: string, dek: CryptoKey): Promise<ArrayBuffer> => {
    if (!base64Data || base64Data.length < 10) {
      throw new Error('無效的金鑰資料');
    }
    try {
      const combined = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
      if (combined.length < 13) {
        throw new Error('金鑰資料太短');
      }
      const iv = combined.slice(0, 12);
      const encrypted = combined.slice(12);
      return await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        dek,
        encrypted
      );
    } catch (e) {
      console.warn('二進位解密失敗:', e);
      throw e;
    }
  };

  // ✅ 用 DEK 加密二進位資料（專門用於 note_key）
  const encryptBinaryWithDEK = async (data: ArrayBuffer, dek: CryptoKey): Promise<string> => {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      dek,
      data
    );
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encrypted), iv.length);
    return btoa(String.fromCharCode(...combined));
  };

  // ========== 匯出備份 ==========

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const confirmExport = confirm(
        '🔐 備份檔案將使用您的登入密碼加密\n\n' +
        '• 備份檔案包含加密後的筆記數據\n' +
        '• 匯入時需要輸入相同的密碼\n' +
        '• 請妥善保管此檔案\n\n' +
        '確定要匯出嗎？'
      );
      if (!confirmExport) {
        setIsExporting(false);
        return;
      }

      const response = await fetch(`/api/notes/backup?userId=${userId}`, {
        headers: { 'X-User-Id': userId }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || '匯出失敗');
      }

      const data = await response.json();

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `notebook-backup-${username}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`✅ 成功匯出 ${data.notes.length} 筆筆記（已加密）`);

    } catch (error) {
      toast.error(error instanceof Error ? error.message : '匯出失敗');
    } finally {
      setIsExporting(false);
    }
  };

  // ========== 匯入備份 - 選擇檔案 ==========

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast.error('請選擇 JSON 格式的備份檔案');
      return;
    }

    setPendingFile(file);
    setInputPassword('');
    setShowPasswordConfirm(true);
    e.target.value = '';
  };

  // ========== 🔐 跨環境導入 ==========

  const handleImportConfirm = async () => {
    if (!pendingFile) return;

    if (!inputPassword.trim()) {
      toast.error('請輸入密碼');
      return;
    }

    if (!currentDEK) {
      toast.error('當前加密金鑰未初始化，請重新登入');
      return;
    }

    setIsVerifying(true);
    setIsImporting(true);
    setImportStatus(null);

    try {
      const text = await pendingFile.text();
      const data = JSON.parse(text);

      if (!data.version || !data.notes || !Array.isArray(data.notes)) {
        throw new Error('無效的備份檔案格式');
      }

      if (data.notes.length === 0) {
        toast('備份檔案中沒有筆記', { icon: '📭' });
        return;
      }

      // ========== 步驟 1：驗證備份檔案 ==========

      const backupSalt = data.user?.salt;
      const backupEncryptedDEK = data.encryptedDEK;

      if (!backupSalt || !backupEncryptedDEK) {
        throw new Error('備份檔案缺少加密金鑰資訊，請使用新版備份');
      }

      // ========== 步驟 2：用密碼解開備份中的 DEK ==========

      const encoder = new TextEncoder();
      const masterKey = await crypto.subtle.importKey(
        'raw',
        encoder.encode(inputPassword),
        'PBKDF2',
        false,
        ['deriveKey']
      );

      const derivedMasterKey = await crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt: encoder.encode(backupSalt),
          iterations: 100000,
          hash: 'SHA-256'
        },
        masterKey,
        {
          name: 'AES-GCM',
          length: 256
        },
        false,
        ['decrypt']
      );

      const combinedData = Uint8Array.from(atob(backupEncryptedDEK), c => c.charCodeAt(0));
      const iv = combinedData.slice(0, 12);
      const encryptedDEK = combinedData.slice(12);

      let backupDEK: CryptoKey;
      try {
        const dekBuffer = await crypto.subtle.decrypt(
          { name: 'AES-GCM', iv: iv },
          derivedMasterKey,
          encryptedDEK
        );
        backupDEK = await crypto.subtle.importKey(
          'raw',
          new Uint8Array(dekBuffer),
          { name: 'AES-GCM' },
          false,
          ['encrypt', 'decrypt']
        );
      } catch (e) {
        throw new Error('密碼錯誤，無法解密備份檔案');
      }

      // ========== 步驟 3：確認 ==========

      const confirmMessage =
        `📋 備份資訊\n\n` +
        `• 筆記數量：${data.notes.length} 筆\n` +
        `• 備份日期：${new Date(data.exportedAt).toLocaleString()}\n` +
        `• 備份使用者：${data.user?.username || '未知'}\n` +
        `• 備份版本：${data.version}\n\n` +
        `⚠️ 此備份來自其他環境，將使用您的當前金鑰重新加密\n\n` +
        `確定要繼續嗎？`;
      if (!confirm(confirmMessage)) {
        return;
      }

			// ========== 步驟 4：解密並重新加密 ==========
			const reEncryptedNotes = [];

			for (const note of data.notes) {
				try {
					let noteKeyBuffer: ArrayBuffer | null = null;
					let noteKeyCrypto: CryptoKey | null = null;

					// 1. 先用舊環境的 backupDEK 解開 note_key 取得原始 32-byte 密鑰
					if (note.note_key) {
						try {
							noteKeyBuffer = await decryptBinaryWithDEK(note.note_key, backupDEK);
						} catch (e) {
							console.warn(`⚠️ 筆記 [${note.id}] 的 note_key 解密失敗:`, e);
						}
					}

					// 2. 將解出的 Raw Key 匯入為 CryptoKey，專門解密這篇筆記的 content/title
					if (noteKeyBuffer && noteKeyBuffer.byteLength === 32) {
						noteKeyCrypto = await crypto.subtle.importKey(
							'raw',
							noteKeyBuffer,
							{ name: 'AES-GCM' },
							false,
							['decrypt', 'encrypt']
						);
					}

					// 3. 欄位解密邏輯（優先用 Note Key，無 Note Key 則降級用 DEK）
					const decryptField = async (encryptedData: string): Promise<string> => {
						if (!encryptedData) return '';
						if (noteKeyCrypto) {
							try {
								return await decryptWithDEK(encryptedData, noteKeyCrypto);
							} catch (e) {
								return await decryptWithDEK(encryptedData, backupDEK);
							}
						}
						return await decryptWithDEK(encryptedData, backupDEK);
					};

					const title = await decryptField(note.title);
					const content = await decryptField(note.content);
					const tags = await decryptField(note.tags);
					const images = await decryptField(note.images);
					const username = await decryptField(note.username);
					const password = await decryptField(note.password);
					const due_date = await decryptField(note.due_date);
					const status = await decryptField(note.status);

					// 4. 重新加密 note_key（用當前環境的 currentDEK）
					let newEncryptedNoteKey = '';
					if (noteKeyBuffer) {
						newEncryptedNoteKey = await encryptBinaryWithDEK(noteKeyBuffer, currentDEK);
					}

					// 5. 重新加密筆記內容（維持使用該篇筆記的 Note Key 加密）
					const encryptField = async (plainText: string): Promise<string> => {
						if (!plainText) return '';
						if (noteKeyCrypto) {
							return await encryptWithDEK(plainText, noteKeyCrypto);
						}
						return await encryptWithDEK(plainText, currentDEK);
					};

					reEncryptedNotes.push({
						...note,
						user_id: userId,
						title: await encryptField(title),
						content: await encryptField(content),
						tags: await encryptField(tags),
						images: await encryptField(images),
						username: await encryptField(username),
						password: await encryptField(password),
						due_date: await encryptField(due_date),
						status: await encryptField(status),
						note_key: newEncryptedNoteKey, // 寫回用當前 DEK 包裹的新 note_key
						updated_at: Date.now(),
					});

				} catch (err) {
					console.error('還原單筆筆記處理失敗:', note.id, err);
				}
			}

      if (reEncryptedNotes.length === 0) {
        throw new Error('沒有筆記可以被導入');
      }

      // ========== 步驟 5：批量上傳 ==========

      const response = await fetch('/api/notes/restore', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId
        },
        body: JSON.stringify({
          notes: reEncryptedNotes
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || '還原失敗');
      }

      setImportStatus({
        success: result.successCount || 0,
        error: result.errorCount || 0
      });

      toast.success(`✅ 成功導入 ${result.successCount || 0} 筆筆記（已轉換為當前帳戶加密）`);

      // ========== 步驟 6：通知父元件 ==========

      if (onImportSuccess) {
        setTimeout(() => {
          onImportSuccess();
        }, 500);
      }

    } catch (error) {
      toast.error(error instanceof Error ? error.message : '還原失敗');
    } finally {
      setIsImporting(false);
      setIsVerifying(false);
      setShowPasswordConfirm(false);
      setPendingFile(null);
      setInputPassword('');
    }
  };

  // ========== UI ==========

  return (
    <div className="space-y-6">
      {/* 標題 */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full mb-3">
          <FileJson className="w-6 h-6 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">備份與還原</h3>
      </div>

      {/* 安全提醒 */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <Lock className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-700 dark:text-blue-300">
            <p className="font-medium">🔐 備份檔案已加密</p>
            <p className="text-xs opacity-80">使用您的登入密碼加密，匯入時需要輸入相同密碼</p>
          </div>
        </div>
      </div>

      {/* 匯出 */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-gray-900 dark:text-white">📤 匯出備份</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              將所有筆記匯出為加密 JSON 檔案
            </div>
          </div>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5 text-sm whitespace-nowrap flex-shrink-0"
          >
            <Download className="w-4 h-4" />
            {isExporting ? '匯出中...' : '匯出備份'}
          </button>
        </div>
      </div>

      {/* 匯入 */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-gray-900 dark:text-white">📥 匯入備份</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              從加密備份檔案還原筆記（支援跨環境導入）
            </div>
          </div>
          <label className={`px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 text-sm whitespace-nowrap flex-shrink-0 ${isImporting ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <Upload className="w-4 h-4" />
            {isImporting ? '匯入中...' : '選擇檔案'}
            <input
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="hidden"
              disabled={isImporting}
            />
          </label>
        </div>
        {importStatus && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-gray-600 dark:text-gray-400">
              成功導入 {importStatus.success} 筆
              {importStatus.error > 0 && (
                <span className="text-red-500">，{importStatus.error} 筆失敗</span>
              )}
            </span>
          </div>
        )}
      </div>

      {/* 密碼確認彈窗 */}
      {showPasswordConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-4">
              <Key className="w-12 h-12 text-blue-500 mx-auto mb-2" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">🔐 驗證密碼</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                請輸入您的登入密碼以解密備份檔案
              </p>
            </div>

            <input
              type="password"
              value={inputPassword}
              onChange={(e) => setInputPassword(e.target.value)}
              placeholder="請輸入密碼"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleImportConfirm();
                }
              }}
            />

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPasswordConfirm(false);
                  setPendingFile(null);
                  setInputPassword('');
                }}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleImportConfirm}
                disabled={isVerifying}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isVerifying ? '驗證中...' : '確認匯入'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 安全提醒 */}
      <div className="text-xs text-gray-400 dark:text-gray-500 text-center border-t border-gray-200 dark:border-gray-700 pt-4">
        <p>⚠️ 匯入將覆蓋同名筆記，建議先匯出備份</p>
      </div>
    </div>
  );
};